/**
 * sb-policy.js — SoundBird policy pages
 * ---------------------------------------------------------------------------
 * Policy pages are rendered by the platform, so the parts of the design that
 * have no markup — the contents rail, the hanging section numbers, the anchors
 * — are built here from the headings the real legal text already has.
 *
 * IT NEVER REWRITES COPY. Every word stays as the store published it. The
 * script wraps, numbers and links; the only text it touches is hiding the
 * leading "1. " each heading already carries, so the design's own number can
 * hang in the margin without the page reading "01  1. Cookies".
 *
 * Vanilla, no dependencies.
 */
(() => {
  const container = document.querySelector('.shopify-policy__container');
  const body = document.querySelector('.shopify-policy__body');
  if (!container || !body) return;

  const rte = body.querySelector('.rte') || body;
  const heads = [...rte.querySelectorAll('h2')];
  if (!heads.length) return;

  /* ── "Last updated" ─────────────────────────────────────────────────
     The design puts this in the header as an uppercase micro-label with a rule
     beside it. Shopify exposes no updated-at for a policy, but the store's own
     text already opens with the line — so it is MOVED, not invented and not
     duplicated. If a policy has no such line the row is simply absent, which is
     the honest outcome; nothing is fabricated to fill it. */
  const title = document.querySelector('.shopify-policy__title');
  const firstPara = rte.querySelector('p');
  if (title && firstPara && /^\s*last updated/i.test(firstPara.textContent)) {
    /* Same reason as the section children below — the intro paragraphs sit in
       the .rte column and carry the same injected margin. */
    rte.querySelectorAll(':scope > p, :scope > ul, :scope > ol').forEach((n) =>
      n.style.setProperty('margin', '0', 'important'));
    const meta = document.createElement('div');
    meta.className = 'sb-pol__meta';
    const span = document.createElement('span');
    span.className = 'sb-pol__updated';
    span.textContent = firstPara.textContent.trim().replace(/\s+/g, ' ');
    const rule = document.createElement('span');
    rule.className = 'sb-pol__rule';
    rule.setAttribute('aria-hidden', 'true');
    meta.append(span, rule);
    title.insertBefore(meta, title.firstChild);
    firstPara.remove();
  }

  const slug = (s) =>
    s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);

  /* Real headings read "1. Personal information we collect". Strip the number
     for display — the design hangs its own, zero-padded — but keep it in the
     accessible name so the anchor and the document agree. */
  const stripNum = (s) => s.replace(/^\s*\d+[.)]\s*/, '').trim();

  const sections = [];

  heads.forEach((h2, i) => {
    const num = String(i + 1).padStart(2, '0');
    const rawTitle = h2.textContent.trim();
    const title = stripNum(rawTitle) || rawTitle;
    const id = `policy-${slug(title)}-${i + 1}`;

    /* Everything up to the next h2 belongs to this section. */
    const owned = [];
    let node = h2.nextSibling;
    while (node && !(node.nodeType === 1 && node.tagName === 'H2')) {
      const next = node.nextSibling;
      owned.push(node);
      node = next;
    }

    const section = document.createElement('section');
    section.className = 'sb-pol__section';
    section.id = id;
    section.setAttribute('aria-labelledby', `${id}-h`);

    const head = document.createElement('div');
    head.className = 'sb-pol__head';
    const numEl = document.createElement('span');
    numEl.className = 'sb-pol__num';
    numEl.setAttribute('aria-hidden', 'true');
    numEl.textContent = num;

    h2.replaceWith(section);
    h2.id = `${id}-h`;
    h2.textContent = title;
    head.append(numEl, h2);

    const bodyWrap = document.createElement('div');
    bodyWrap.className = 'sb-pol__body';
    owned.forEach((n) => {
      bodyWrap.appendChild(n);
      /* Margins are zeroed HERE, not in the stylesheet. Shopify's injected
         policy CSS sets the logical margin-block-end on these elements from a
         sheet that loads after the theme's, so no selector we can write wins on
         order — a list kept a 14px bottom margin that stacked with the column
         gap, and every list sat 24px clear of the next paragraph while every
         other pair sat at 10. An inline !important is above all of it, and this
         is the one place that already owns these nodes. Spacing is the flex
         column's gap, and only that. */
      if (n.nodeType === 1) n.style.setProperty('margin', '0', 'important');
    });

    section.append(head, bodyWrap);
    sections.push({ id, num, title });
  });

  /* ── Contents rail ──────────────────────────────────────────────────
     Built from the headings that exist, so it can never disagree with the
     document — a hand-kept list would drift the first time legal text changed. */
  const toc = document.createElement('nav');
  toc.className = 'sb-pol__toc';
  toc.setAttribute('aria-label', 'On this page');

  const inner = document.createElement('div');
  inner.className = 'sb-pol__toc-inner';

  const label = document.createElement('div');
  label.className = 'sb-pol__toc-label';
  label.textContent = 'On this page';

  /* Mobile shows the same list behind a toggle; desktop shows it outright. The
     button is always in the DOM so the control is real at both widths, and CSS
     decides which presentation applies. */
  const toggle = document.createElement('button');
  toggle.className = 'sb-pol__toc-toggle';
  toggle.type = 'button';
  toggle.setAttribute('aria-expanded', 'false');
  toggle.innerHTML =
    '<span>On this page</span>' +
    '<span class="sb-pol__toc-caret"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg></span>';

  const collapse = document.createElement('div');
  collapse.className = 'sb-pol__toc-collapse';
  const list = document.createElement('div');
  list.className = 'sb-pol__toc-list';

  sections.forEach((s) => {
    const a = document.createElement('a');
    a.className = 'sb-pol__toc-link';
    a.href = `#${s.id}`;
    a.innerHTML =
      `<span class="sb-pol__toc-num" aria-hidden="true">${s.num}</span>` +
      `<span class="sb-pol__toc-text"></span>`;
    a.querySelector('.sb-pol__toc-text').textContent = s.title;
    list.appendChild(a);
  });

  collapse.appendChild(list);

  /* The "Download PDF" button was removed 2026-08-05 at the owner's request. It
     never produced a PDF — it called window.print() and relied on the visitor
     choosing "Save as PDF" in the print dialogue, which is not what the label
     promised. The print stylesheet below is kept: printing a policy still works
     from the browser's own menu and still looks right. */
  inner.append(label, toggle, collapse);
  toc.appendChild(inner);
  container.insertBefore(toc, body);

  /* ── Mobile collapse ── measured height, never auto. */
  let open = false;
  const setOpen = (next) => {
    open = next;
    toc.toggleAttribute('data-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    collapse.style.height = open ? `${list.getBoundingClientRect().height + 12}px` : '0px';
  };
  toggle.addEventListener('click', () => setOpen(!open));
  addEventListener('resize', () => { if (open) setOpen(true); }, { passive: true });

  /* Anchor clicks: smooth where allowed, and the section's scroll-margin keeps
     the heading clear of the fixed pill. Collapsing on mobile after a jump
     stops the card covering what was just navigated to. */
  list.addEventListener('click', (e) => {
    const link = e.target.closest('.sb-pol__toc-link');
    if (!link) return;
    const target = document.querySelector(link.getAttribute('href'));
    if (!target) return;
    e.preventDefault();
    history.replaceState(null, '', link.getAttribute('href'));

    /* The contents card sits ABOVE every section, so its collapse moves the
       target. Closing it after the jump left the heading behind the header;
       closing it and scrolling a frame later OVERSHOT by ~280px, because the
       collapse is a 400ms transition and the scroll was aimed at a position
       that kept changing under it.
       So: collapse, wait for it to actually finish, then scroll. The timeout is
       not belt-and-braces — transitionend never fires in a backgrounded tab,
       and the jump has to work there too. */
    const go = () => {
      const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    };
    if (!open) { go(); return; }

    let fired = false;
    const once = (ev) => {
      if (fired || (ev && ev.target !== collapse)) return;
      fired = true;
      collapse.removeEventListener('transitionend', once);
      go();
    };
    collapse.addEventListener('transitionend', once);
    setTimeout(once, 450);
    setOpen(false);
  });

  /* Deep links. The ids are created here, so at parse time they do not exist
     and the browser's own jump finds nothing — /policies/x#section would just
     load at the top. Re-aim once the sections are real. */
  if (location.hash) {
    const deep = document.querySelector(location.hash);
    if (deep) requestAnimationFrame(() => deep.scrollIntoView({ behavior: 'auto', block: 'start' }));
  }
})();
